// Row of per-dimension chips in the topbar. Each chip is its own
// dropdown trigger: click "Location" and a small popover lists every
// location value to filter the current page by. The popover supports
// multi-select via checkboxes; hit Apply and every ticked value is
// added to the page scope at once. Already-active values come up
// pre-ticked and un-ticking them removes them from scope on Apply.
//
// "Browse all <dimension>" stays as an escape hatch that drops into
// the meta-list view, useful when the admin wants to see the full
// list of values up front.
//
// Integration hook: pure presentation. Values arrive from the data
// aggregates so no API surface is owned here.
import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
    Box, Popover, TextField, Checkbox, Button, Typography, InputAdornment,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { DEPTS, LOCS, CITIES, COUNTRIES, COMPANIES, STATES } from '../data';
import { selectsEveryOption } from '../serverScope';
import { pluralLabel } from '../helpers';

const TYPES = [
    // 'source' getters read the live ./data bindings at access time so the
    // lists refresh after each hydration (a plain property would capture the
    // pre-hydration empty arrays).
    { id: 'department', label: 'Department', get source() { return DEPTS; } },
    { id: 'location',   label: 'Location',   get source() { return LOCS; } },
    { id: 'city',       label: 'City',       get source() { return CITIES; } },
    { id: 'country',    label: 'Country',    get source() { return COUNTRIES; } },
    { id: 'company',    label: 'Company',    get source() { return COMPANIES; } },
    { id: 'state',      label: 'State',      get source() { return STATES; } },
];

// Trigger styling shared by every topbar filter chip; filled when active.
export const chipSx = (isActive) => ({
    display: 'inline-flex', alignItems: 'center', gap: 0.5,
    px: 1.25, py: '4px',
    borderRadius: '4px',
    border: '1px solid',
    borderColor: isActive ? 'secondary.main' : 'divider',
    bgcolor: isActive ? 'secondary.main' : 'transparent',
    color: isActive ? 'common.white' : 'text.secondary',
    fontSize: 11, fontWeight: 600,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 120ms, color 120ms, border-color 120ms',
    '&:hover': { borderColor: 'secondary.main', color: isActive ? 'common.white' : 'secondary.main' },
    '&:focus-visible': { outline: '2px solid', outlineColor: 'secondary.main', outlineOffset: 1 },
});

// Chip + searchable multi-select popover: the one filter control used for
// every dimension across the report pages (Department, Location, Simulation,
// User, course and the send-history boxes). Options are { label, value };
// active values arrive pre-ticked; Apply reports the delta plus the full
// selection so callers can drive either scope drills or local state.
//
// `outside` ({ options, label, emptyLabel }) adds a collapsed second section
// below the main list, for values that exist but fall outside the current
// filters (BD-1284: sims and courses not sent in the selected dates). It opens
// from a "Show N ..." row, which counts only the matches while searching, and
// is forced open while an applied value sits in it so that value can be
// unticked. Otherwise every open starts with it collapsed again. Chips without
// `outside` render as before.
const ROW_SX = {
    display: 'flex', alignItems: 'center', gap: 0.5,
    px: 0.5, py: 0.1, mx: 0.5, borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': { bgcolor: 'action.hover' },
    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
};
const LINK_ROW_SX = {
    display: 'flex', alignItems: 'center',
    px: 1, py: 0.5, mx: 0.5, borderRadius: '4px',
    cursor: 'pointer',
    fontSize: 11, fontWeight: 600, color: 'text.disabled',
    '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};
const onActivate = (fn) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } };
const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export function FilterChip({ label, allLabel = null, options, activeValues = [], onApply, onBrowseAll = null, searchPlaceholder, onQueryChange = null, remoteOptions = null, remotePending = false, outside = null }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(() => new Set(activeValues));
    const [remoteLabels, setRemoteLabels] = useState(() => new Map());
    const [showOutside, setShowOutside] = useState(false);
    const searchRef = useRef(null);
    const activeKey = activeValues.join('\u0000');
    const outsideOptions = outside?.options;

    const updateQuery = (value) => {
        setQuery(value);
        onQueryChange?.(value);
    };

    useEffect(() => {
        if (anchorEl) {
            setQuery('');
            onQueryChange?.('');
            setSelected(new Set(activeValues));
            setShowOutside(false);
            setTimeout(() => searchRef.current?.focus(), 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorEl, activeKey]);

    useEffect(() => {
        if (!remoteOptions?.length) return;
        setRemoteLabels((prev) => {
            const next = new Map(prev);
            remoteOptions.forEach((o) => next.set(o.value, o.label));
            return next;
        });
    }, [remoteOptions]);

    const labelByValue = useMemo(() => {
        const m = new Map(remoteLabels);
        (outsideOptions || []).forEach((o) => m.set(o.value, o.label));
        (options || []).forEach((o) => m.set(o.value, o.label));
        return m;
    }, [options, outsideOptions, remoteLabels]);
    const outsideMatches = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? (outsideOptions || []).filter((o) => o.label.toLowerCase().includes(q)) : (outsideOptions || []);
        return list.slice(0, 500);
    }, [outsideOptions, query]);
    const outsideForcedOpen = useMemo(
        () => (outsideOptions || []).some((o) => activeValues.includes(o.value)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [outsideOptions, activeKey]
    );
    const outsideOpen = showOutside || outsideForcedOpen;
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? (options || []).filter((o) => o.label.toLowerCase().includes(q)) : (options || []);
        const local = list.slice(0, 500);
        if (!q || !remoteOptions?.length) return local;
        const seen = new Set(local.map((o) => o.value));
        return [...local, ...remoteOptions.filter((o) => !seen.has(o.value))];
    }, [options, query, remoteOptions]);

    const open = (e) => setAnchorEl(e.currentTarget);
    const close = () => {
        setAnchorEl(null);
        onQueryChange?.('');
    };

    const toggleValue = (value) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    };

    const apply = () => {
        const before = new Set(activeValues);
        const after = selected;
        const added = [];
        const removed = [];
        after.forEach((v) => { if (!before.has(v)) added.push(v); });
        before.forEach((v) => { if (!after.has(v)) removed.push(v); });
        onApply({ added, removed, values: [...after] });
        close();
    };

    const clearAll = () => setSelected(new Set());

    const renderOption = (o, muted = false) => (
        <Box
            key={o.value}
            role="button"
            tabIndex={0}
            onClick={() => toggleValue(o.value)}
            onKeyDown={onActivate(() => toggleValue(o.value))}
            sx={ROW_SX}
        >
            <Checkbox
                checked={selected.has(o.value)}
                size="small"
                tabIndex={-1}
                readOnly
                sx={{ p: 0.5, pointerEvents: 'none' }}
            />
            <Typography sx={{ fontSize: 12, color: muted ? 'text.secondary' : 'text.primary', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {o.label}
            </Typography>
        </Box>
    );

    const isActive = activeValues.length > 0;
    const allSelected = allLabel != null && selectsEveryOption(options, activeValues);
    const chipLabel = allSelected
        ? `${label} (${allLabel})`
        : isActive && activeValues.length > 1
        ? `${label} (${activeValues.length})`
        : isActive
            ? `${label}: ${labelByValue.get(activeValues[0]) || activeValues[0]}`
            : label;

    return (
        <>
            <Box
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); } }}
                sx={{ ...chipSx(isActive), maxWidth: 240 }}
            >
                <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {chipLabel}
                </Box>
                <KeyboardArrowDownIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            </Box>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        mt: 0.5, width: 280,
                        border: '1px solid', borderColor: 'divider', borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <Box sx={{ p: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <TextField
                        inputRef={searchRef}
                        value={query}
                        onChange={(e) => updateQuery(e.target.value)}
                        placeholder={searchPlaceholder || `Search ${label.toLowerCase()}`}
                        size="small"
                        fullWidth
                        // Chrome autofills focused bare inputs with remembered emails;
                        // this is a transient search box, never a form field.
                        inputProps={{ autoComplete: 'off', name: 'rbx-filter-search' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            sx: { fontSize: 12, borderRadius: '6px' },
                        }}
                    />
                </Box>

                <Box sx={{ maxHeight: 260, overflowY: 'auto', py: 0.5 }}>
                    {onBrowseAll && (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => { onBrowseAll(); close(); }}
                            onKeyDown={onActivate(() => { onBrowseAll(); close(); })}
                            sx={LINK_ROW_SX}
                        >
                            Browse all {pluralLabel(label).toLowerCase()}
                        </Box>
                    )}
                    {visible.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: outsideMatches.length ? 1.5 : 3, fontSize: 11, color: 'text.disabled' }}>
                            {remotePending ? 'Searching…' : outsideMatches.length ? outside.emptyLabel : 'No values match'}
                        </Box>
                    ) : visible.map((o) => renderOption(o))}
                    {outsideMatches.length > 0 && (
                        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            {outsideOpen ? (
                                <>
                                    <Typography sx={{ px: 1, py: 0.5, mx: 0.5, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                                        {outside.label}
                                    </Typography>
                                    {outsideMatches.map((o) => renderOption(o, true))}
                                    {!outsideForcedOpen && (
                                        <Box role="button" tabIndex={0} onClick={() => setShowOutside(false)} onKeyDown={onActivate(() => setShowOutside(false))} sx={LINK_ROW_SX}>
                                            Hide
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Box role="button" tabIndex={0} onClick={() => setShowOutside(true)} onKeyDown={onActivate(() => setShowOutside(true))} sx={LINK_ROW_SX}>
                                    Show {outsideMatches.length} {query.trim() ? (outsideMatches.length === 1 ? 'match ' : 'matches ') : ''}{lowerFirst(outside.label)}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        size="small"
                        onClick={clearAll}
                        disabled={selected.size === 0}
                        sx={{ fontSize: 11, textTransform: 'none', color: 'text.secondary' }}
                    >
                        Clear
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={apply}
                        sx={{ fontSize: 11, textTransform: 'none' }}
                    >
                        Apply{selected.size > 0 ? ` (${selected.size})` : ''}
                    </Button>
                </Box>
            </Popover>
        </>
    );
}

function DimensionChip({ type, serverOptions, activeValues, onApply, onBrowseAll }) {
    // Prefer the server-side dim_values options (the whole tenancy subtree) so
    // child-tenancy values that never reached the bounded client roster still
    // appear; fall back to the client-derived ./data list while the aggregation
    // is still loading (or empty), so the dropdown is never blank.
    const options = Array.isArray(serverOptions) && serverOptions.length
        ? serverOptions.map((name) => ({ label: name, value: name }))
        : (type.source || []).filter((d) => d.name).map((d) => ({ label: d.name, value: d.name }));
    return (
        <FilterChip
            label={type.label}
            options={options}
            activeValues={activeValues}
            onApply={onApply}
            onBrowseAll={onBrowseAll}
            searchPlaceholder={`Search ${type.label.toLowerCase()}`}
        />
    );
}

export default function MetaNav({
    drill,
    removeScope,
    scope = {},
    activeType = null,
    noPadX = false,
    exclude = [],
    currentPage = 'sims',
    currentLabel = 'Simulations',
    // Per-dimension option lists from the server dim_values aggregation
    // ({ department: [name, ...], ... }), covering the whole tenancy subtree.
    // Null while it loads, in which case each chip falls back to the bounded
    // client ./data list.
    dimOptions = null,
    // Page-specific chips (Simulation / Course / User) rendered inline with the
    // dimension chips so every filter for the page sits on one line.
    extraChips = null,
}) {
    const excludeSet = new Set(exclude);
    const visibleTypes = useMemo(() => TYPES.filter((t) => !excludeSet.has(t.id)), [exclude]);

    if (visibleTypes.length === 0 && !extraChips) return null;

    // scope arrives keyed by dimension id (e.g. { department: ['Finance'] }).
    // Normalise to an array per type so DimensionChip can read it directly.
    const activeFor = (typeId) => {
        const v = scope[typeId];
        if (Array.isArray(v)) return v;
        if (typeof v === 'string') return [v];
        return [];
    };

    const apply = (type, { added, removed }) => {
        // Removed values come off the scope via removeScope so the
        // stack stays clean rather than accumulating empty entries.
        for (const value of removed) removeScope && removeScope(type.id, value);
        // Added values each get their own stack entry on the current
        // page. The scope reducer in index.js folds duplicates into a
        // single { type: [...] } so multiple ticks survive cleanly.
        for (const value of added) drill(currentPage, `${currentLabel} - ${value}`, { type: type.id, value });
    };

    const browseAll = (type) => {
        drill('meta-list', type.label, { type: 'metaType', value: type.id });
    };

    return (
        <Box sx={{ padding: noPadX ? 0 : '0 24px', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {visibleTypes.map((t) => (
                <DimensionChip
                    key={t.id}
                    type={t}
                    serverOptions={dimOptions && dimOptions[t.id]}
                    activeValues={activeFor(t.id)}
                    onApply={(changes) => apply(t, changes)}
                    onBrowseAll={() => browseAll(t)}
                />
            ))}
            {extraChips}
        </Box>
    );
}
